import { useSelection } from "@/contexts/SelectionContext";
import { saveScrollTarget } from "@/lib/utils/scrollRestoration";
import { withToast } from "@/lib/utils/withToast";
import { useNavigate } from "@tanstack/react-router";
import { useMoveLessons } from "../lessons/useMoveLessons";
import { useMoveProjects } from "../projects/useMoveProjects";

export function useMoveItems() {
  const { moveProjects, loading: moveProjectsLoading } = useMoveProjects();
  const { moveLessons, loading: moveLessonsLoading } = useMoveLessons();

  const { selectedProjectIds, selectedLessonIds, clearAllSelections, toggleWarningModal } =
    useSelection();

  const navigate = useNavigate();

  const moveItems = async (targetProjectId: string | null, sourceProjectId: string | null) => {
    const totalItems = selectedProjectIds.length + selectedLessonIds.length;

    try {
      await withToast(
        async () => {
          // Create array of promises for parallel execution
          const promises: Promise<unknown>[] = [];

          if (selectedProjectIds.length > 0) {
            promises.push(moveProjects(selectedProjectIds, targetProjectId, sourceProjectId));
          }

          if (selectedLessonIds.length > 0) {
            promises.push(moveLessons(selectedLessonIds, targetProjectId, sourceProjectId));
          }

          // Wait for all moves to complete
          if (promises.length > 0) {
            await Promise.all(promises);
          }

          // Save scroll target for the first moved item
          const pageKey = targetProjectId ?? "home";
          if (selectedProjectIds.length > 0) {
            saveScrollTarget(`project-${selectedProjectIds[0]}`, pageKey);
          } else if (selectedLessonIds.length > 0) {
            saveScrollTarget(`lesson-${selectedLessonIds[0]}`, pageKey);
          }

          if (targetProjectId) {
            navigate({
              to: "/admin/project/$projectId",
              params: { projectId: targetProjectId },
            });
          } else {
            navigate({ to: "/admin" });
          }

          clearAllSelections();
        },
        {
          loading: `Moving ${totalItems} item${totalItems !== 1 ? "s" : ""}...`,
          success: `Successfully moved ${totalItems} item${totalItems !== 1 ? "s" : ""}`,
          error: "Failed to move items",
        },
        "move-items"
      );
    } catch (error) {
      toggleWarningModal("Failed to move items", undefined, error);
    }
  };

  return {
    moveItems,
    loading: moveLessonsLoading || moveProjectsLoading,
  };
}
